import React, { useEffect } from 'react';
import { MapContainer, Marker, useMap, useMapEvents } from 'react-leaflet';
import BaseTileLayer from './BaseTileLayer';
import MapAutoResize from './MapAutoResize';
import LocationSearch from './LocationSearch';
import './leafletSetup';
import './LocationPicker.css';

// Buenos Aires — centro de arranque cuando ni el campo ni el device tienen
// todavía una posición cargada.
const DEFAULT_CENTER: [number, number] = [-34.6037, -58.3816];

function ClickHandler({ onChange }: { onChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Si la posición cambia desde afuera del mapa (botón "usar mi ubicación", o
// el GPS del equipo), el mapa la sigue en vez de quedarse mirando otro lado.
function FollowPosition({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => {
    if (lat === null || lng === null) return;
    if (!map.getBounds().pad(-0.1).contains([lat, lng])) {
      map.setView([lat, lng], Math.max(map.getZoom(), 15));
    }
  }, [lat, lng, map]);
  return null;
}

interface LocationPickerProps {
  lat: number | null;
  lng: number | null;
  onChange: (lat: number, lng: number) => void;
  center?: [number, number];
  height?: number;
  // Buscador de localidad / coordenadas arriba del mapa.
  searchable?: boolean;
}

// Mapa embebido de click-para-marcar: se usa tanto al crear/editar un Campo
// como al asignarle posición a un Device dentro de un Campo. Arriba, un
// buscador para cuando no se sabe dónde tocar: localidad o coordenadas.
const LocationPicker: React.FC<LocationPickerProps> = ({ lat, lng, onChange, center, height = 220, searchable = true }) => {
  const hasPosition = lat !== null && lng !== null;
  const initialCenter: [number, number] = hasPosition ? [lat as number, lng as number] : center ?? DEFAULT_CENTER;

  return (
    <>
      {searchable && <LocationSearch onPick={onChange} />}
      <div style={{ height, borderRadius: 12, overflow: 'hidden', margin: '8px 0' }}>
        <MapContainer center={initialCenter} zoom={hasPosition ? 16 : 12} style={{ height: '100%', width: '100%' }}>
          <BaseTileLayer />
          <MapAutoResize />
          <ClickHandler onChange={onChange} />
          <FollowPosition lat={lat} lng={lng} />
          {hasPosition && <Marker position={[lat as number, lng as number]} />}
        </MapContainer>
      </div>
    </>
  );
};

export default LocationPicker;
